import { CONFIRMATION_TYPES } from '../../../enums/confirmation-types.enum';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { ConfirmationState } from '../confirmation-collection.state';

/**
 * Whether `confirmation` is the stored one for `notificationId`/`type`. The slice compares identities
 * structurally rather than through a derived key string, so the confirmation's own
 * `notificationId` and `confirmationType` stay the only representation of its identity.
 *
 * @param confirmation the stored confirmation to test
 * @param notificationId the notification the caller is asking about
 * @param type the confirmation type the caller is asking about
 * @returns `true` when the confirmation belongs to that notification and type
 */
export function isConfirmationFor(
    confirmation: ConfirmationState,
    notificationId: NotificationIdState,
    type: CONFIRMATION_TYPES,
): boolean {
    const stored = confirmation.notificationId;
    return (
        confirmation.confirmationType === type &&
        stored.frontId === notificationId.frontId &&
        stored.qmNum === notificationId.qmNum &&
        stored.maNum === notificationId.maNum
    );
}

/**
 * The status a notification moves to when the technician defers signing.
 *
 * Pure policy, so it is stated once here rather than inside the effect that saves the status: a
 * confirmation on the road to signing steps back to its "to be signed" counterpart, one already there
 * stays put, and anything else starts that road at `TOBS`.
 *
 * @param current the notification's status right now
 * @returns the status to save, which may be the one it already has
 */
export function nextSignLaterStatus(current: NOTIFICATION_STATUS_CODE): NOTIFICATION_STATUS_CODE {
    switch (current) {
        case NOTIFICATION_STATUS_CODE.ROSR:
            return NOTIFICATION_STATUS_CODE.RTOBS;
        case NOTIFICATION_STATUS_CODE.FCC:
            return NOTIFICATION_STATUS_CODE.FTOBS;
        case NOTIFICATION_STATUS_CODE.RTOBS:
        case NOTIFICATION_STATUS_CODE.FTOBS:
            return current;
        default:
            return NOTIFICATION_STATUS_CODE.TOBS;
    }
}
